import {
    Assessment,
    AssessmentResourceAction,
    CreateAssessmentPermissionInput,
    DeleteAssessmentPermissionInput,
    ResourcePermission,
} from '@amzn/awscat-aws-assessment-service-typescript-client';
import { UserInfo, UserType } from '@amzn/awscat-portal-authentication-library';
import { AuthContextInterface, withAuthContext } from '@amzn/awscat-react-components';
import {
    Alert,
    Box,
    Button,
    ColumnLayout,
    Container,
    FormField,
    Header,
    Input,
    Modal,
    Select,
    SpaceBetween,
    TextContent,
    Tiles,
} from '@amzn/awsui-components-react';
import { PC30_PLACEHOLDER_EMAIL, isPC30IdentityMigrationEnabled, isPC30UserUserId } from '@amzn/caracal-infrastructure-common';
import { IsEmail, ValidationError, isEmail, validate } from 'class-validator';
import React, { useCallback, useEffect, useState } from 'react';

import ShareTable, { Change, ShareViewModel } from './PermissionsTable';
import ShareInvitationConfirmationDialog from './ShareInvitationConfirmationDialog';
import ShareMultiUserSelectDialog from './ShareMultiUserSelectDialog';
import { GetEnvironment } from '../../../Environments';
import a2sClient from '../../../api/a2s/A2SClient';
import DataCollectionClient, { ExistingQuestionnaire } from '../../../api/data-collection/DataCollectionClient';
import { AppLabels } from '../../../common/AppLabels';
import { AppLabelsContextInterface, withAppLabelsContext } from '../../../common/AppLabelsContext';
import Constants from '../../../common/Constants';
import { logger } from '../../../common/Logger';
import {
    constructUserIdFromIamIdentity,
    extractAmazonEmailFromUserId,
    getUserIdFromAmazonUserEmail,
    validateIamIdentityName,
} from '../../../common/Utils';
import { errorLookup } from '../../../common/ValidatorUtils';
import rumClient from '../../../common/monitoring/RumClient';
import { Action, ActionType } from '../AssessmentActions';
import { AssessmentViewModel } from '../AssessmentViewModel';
import { AssessmentPermissionViewModel } from '../PermissionViewModel';

enum ShareInputType {
    EMAIL = 'email',
    IAM_IDENTITY = 'iam_identity',
}

enum ShareUserType {
    AWS = 'AWS',
    PARTNER = 'PARTNER',
    PC30 = 'PC30',
}

export class ShareAssessmentUserInput {
    @IsEmail(undefined, {
        message: 'assessment_share_error_invalid_username',
    })
    userEmail: string;
}

type Conclusion = {
    newPermissions: AssessmentPermissionViewModel[];
    changedPermissions: AssessmentPermissionViewModel[];
    deletedPermissions: AssessmentPermissionViewModel[];
};

interface Props {
    assessment: AssessmentViewModel;
    assessmentActionDispatch: React.Dispatch<Action>;
}

/**
 * Delete Permissions associated with Assessments
 */
const deletePermission = async (assessment: AssessmentViewModel, p: AssessmentPermissionViewModel) => {
    let apiResult = null;
    if (p.invitationId) {
        // This is a pending invitation
        apiResult = await a2sClient?.deleteAssessmentPermissionInvitation(p.invitationId);
    } else {
        const deleteInput: DeleteAssessmentPermissionInput = {
            assessmentId: assessment.id,
            userId: p.userId,
            action: p.action,
        };
        apiResult = await a2sClient?.deleteAssessmentPermission(deleteInput);
    }

    if (apiResult?.errors) {
        throw new Error(apiResult.errors.toString());
    }
};

/**
 * Add Permissions to associate with Assessments
 */
const createPermission = async (assessment: AssessmentViewModel, p: AssessmentPermissionViewModel) => {
    const createInput: CreateAssessmentPermissionInput = {
        assessmentId: assessment.id,
        action: p.action,
        assessmentLink: `https://${window.location.host}/assessments/${assessment.id}`,
        inviteeEmail: p.inviteeEmail || PC30_PLACEHOLDER_EMAIL,
        userId: p.userId,
    };
    const apiResult = await a2sClient?.createAssessmentPermission(createInput);
    if (apiResult?.errors) {
        throw new Error(apiResult.errors.toString());
    }
};

const getQuestionnaire = async (assessmentId: string): Promise<ExistingQuestionnaire | undefined> => {
    const sourceResourceId = `awscat:a2s:${assessmentId}`;
    const questionnaires = await DataCollectionClient.getQuestionnaires(sourceResourceId);
    const existingQuestionnaire = questionnaires.length ? questionnaires[0] : undefined;
    return existingQuestionnaire;
};

const updateQuestionnaire = async (questionnaireId: string, owners: string[]): Promise<any> => {
    const updatedQuestionnaire: Partial<ExistingQuestionnaire> = {
        owners,
    };
    const result = await DataCollectionClient.patchQuestionnaire(questionnaireId, updatedQuestionnaire);
    return result;
};

/**
 * Remove Permissions associated with Pre-Event Questionnaire
 */
const removePreEventPermission = async (assessment: AssessmentViewModel, p: AssessmentPermissionViewModel) => {
    const existingQuestionnaire = await getQuestionnaire(assessment.id);
    if (existingQuestionnaire) {
        const email = p.userEmail ?? p.inviteeEmail ?? extractAmazonEmailFromUserId(p.userId);
        const newOwners = existingQuestionnaire.owners.filter((owner) => owner !== email);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const result = await updateQuestionnaire(existingQuestionnaire.id, newOwners);
        return;
    }
};

/**
 * Add Permissions to associate Pre-Event Questionnaire
 */
const addPreEventPermission = async (assessment: AssessmentViewModel, p: AssessmentPermissionViewModel) => {
    const existingQuestionnaire = await getQuestionnaire(assessment.id);
    if (existingQuestionnaire) {
        const email: string = p.userEmail ?? p.inviteeEmail ?? extractAmazonEmailFromUserId(p.userId);
        // Skip adding PC30_PLACEHOLDER_EMAIL to questionnaire owners
        if (email === PC30_PLACEHOLDER_EMAIL) {
            return;
        }
        const newOwners: string[] = existingQuestionnaire.owners || []; // just in case it's not initialized
        newOwners.push(email);
        existingQuestionnaire.owners = [...new Set(newOwners)]; // remove any potential duplicates
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const result = await updateQuestionnaire(existingQuestionnaire.id, newOwners);
        return;
    }
};

/**
 * Remove Permissions associated with both Assessments and Pre-Event Questionnaire
 */
export const deletePermissions = async (assessment: AssessmentViewModel, deletedPermissions: AssessmentPermissionViewModel[]): Promise<void> => {
    for (const p of deletedPermissions) {
        await deletePermission(assessment, p);
        await removePreEventPermission(assessment, p);
    }
};

/**
 * Add Permissions to associate with both Assessments and Pre-Event Questionnaire
 */
export const createPermissions = async (assessment: AssessmentViewModel, createdPermissions: AssessmentPermissionViewModel[]): Promise<void> => {
    for (const p of createdPermissions) {
        await createPermission(assessment, p);
        await addPreEventPermission(assessment, p);
    }
};

const ShareAssessmentDialog = (props: Props & AuthContextInterface & AppLabelsContextInterface): JSX.Element => {
    const appLabels: AppLabels = props.appLabels;

    // Check if PC30 identity migration feature is enabled
    const isFeatureEnabled = isPC30IdentityMigrationEnabled(GetEnvironment());

    // Determine user type for sharing mode
    const shareUserType: ShareUserType = (() => {
        if (!isFeatureEnabled) return ShareUserType.PARTNER;
        try {
            const userInfo = props.auth && props.auth.isAuthenticated() ? props.auth.getUserInfo() : undefined;
            if (!userInfo?.userId) return ShareUserType.PARTNER;
            if (isPC30UserUserId(userInfo.userId)) return ShareUserType.PC30;
            if (userInfo.organizationId === 'AWS') return ShareUserType.AWS;
            return ShareUserType.PARTNER;
        } catch (error) {
            return ShareUserType.PARTNER;
        }
    })();

    const [shareInputType, setShareInputType] = useState<ShareInputType>(
        isFeatureEnabled && shareUserType === ShareUserType.PC30 ? ShareInputType.IAM_IDENTITY : ShareInputType.EMAIL
    );

    const [newUserAliasOrEmail, setUserAliasOrEmail] = useState('');
    const [newUserAlias, setNewUserAlias] = useState('');
    const [newUserIamIdentityName, setNewUserIamIdentityName] = useState('');
    const [newPermission, setNewPermission] = useState<AssessmentResourceAction>(AssessmentResourceAction.VIEW);
    const [updateInProgress, setUpdateInProgress] = useState(false);
    const [errorAlertVisible, setErrorAlertVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const getValidationErrorText = errorLookup<ShareAssessmentUserInput>(validationErrors);
    const getErrorText = (attribute: keyof ShareAssessmentUserInput): string | undefined => {
        const validationErrorText = getValidationErrorText(attribute);
        return validationErrorText ? appLabels.intlGet(validationErrorText) : '';
    };
    const [inviteUserEmail, setInviteUserEmail] = useState<string>('');
    const [usersToSelectFrom, setUsersToSelectFrom] = useState<UserInfo[]>([]);
    const [aliasErrorMessage, setAliasErrorMessage] = useState('');
    const [iamIdentityNameErrorMessage, setIamIdentityNameErrorMessage] = useState('');

    // This tracks the contents of the table
    const [permissions, setPermissions] = useState<ShareViewModel[]>([]);

    const validUserName = newUserAliasOrEmail.length > 0;

    const updatePermissions = async (
        newPermissions: AssessmentPermissionViewModel[],
        changedPermissions: AssessmentPermissionViewModel[],
        deletedPermissions: AssessmentPermissionViewModel[]
    ) => {
        const assessment = props.assessment;

        if (logger.level === 'DEBUG' || logger.level === 'VERBOSE') {
            logger.debug('shareItemAsync()');
            logger.debug('newPermissions: ', newPermissions);
            logger.debug('changedPermissions: ', changedPermissions);
            logger.debug('deletedPermissions: ', deletedPermissions);
        }

        // 1. delete the deletedPermissions
        await deletePermissions(assessment, deletedPermissions);
        // 2. delete the changedPermissions
        await deletePermissions(assessment, changedPermissions);
        // 3. create the changedPermissions
        await createPermissions(assessment, changedPermissions);
        // 4. create the newPermissions
        await createPermissions(assessment, newPermissions);
    };

    const onConfirmShare = async (conclusion: Conclusion) => {
        setUpdateInProgress(true);
        try {
            await updatePermissions(conclusion.newPermissions, conclusion.changedPermissions, conclusion.deletedPermissions);
            // successfully shared assessment, reload tables
            props.assessmentActionDispatch({ type: ActionType.RELOAD_ASSESSMENTS });
        } catch (err: any) {
            rumClient.recordError(err);
            const errorMessage = `${err.errors[0]?.message || appLabels.assessment.share.error_update_permissions}`;
            const conclusionObject = JSON.stringify(conclusion);
            logger.info(`Error encountered while adding permissions: ${errorMessage}. Conclusion Object details: ${conclusionObject}`);
            setErrorMessage(errorMessage);
            setErrorAlertVisible(true);
        }
        setUpdateInProgress(false);
    };

    const cancelAction = useCallback(() => {
        props.assessmentActionDispatch({ type: ActionType.CANCEL });
    }, [props]);

    const resetAddUser = useCallback(() => {
        setUserAliasOrEmail('');
        setNewUserAlias('');
        setNewUserIamIdentityName('');
        setInviteUserEmail('');
        setUsersToSelectFrom([]);
        setNewPermission(AssessmentResourceAction.VIEW);
    }, []);

    const inviteUserAction = useCallback(async () => {
        let userEmail = newUserAliasOrEmail;
        if (!isEmail(newUserAliasOrEmail)) {
            const myEmail = props.auth.getUserInfo()?.email;
            const myEmailDomain = myEmail.split('@')[1];
            userEmail = `${newUserAliasOrEmail}@${myEmailDomain}`;
        }
        const newPerms = permissions.slice();
        newPerms.push({
            action: newPermission,
            userEmail,
            change: Change.Invite,
            userId: undefined,
            inviteeEmail: userEmail,
        });
        setPermissions(newPerms);
        resetAddUser();
    }, [newPermission, newUserAliasOrEmail, permissions, props.auth, resetAddUser]);

    const addSelectedUsersAction = useCallback(
        async (selectedUsers: UserInfo[]) => {
            const newPerms = permissions.slice();
            selectedUsers.forEach((u) => {
                // check if user permission already granted
                const userAlreadyHasPermission = permissions.find((p) => p.userId === u.userId && p.action === newPermission);
                if (!userAlreadyHasPermission) {
                    const userEmail = u.email || PC30_PLACEHOLDER_EMAIL;
                    newPerms.push({
                        action: newPermission,
                        userEmail,
                        change: Change.Add,
                        userId: u.userId,
                        inviteeEmail: userEmail,
                        userName: u.name || u.displayName,
                        userOrgName: u.organizationName || u.organizationId,
                    });
                }
            });
            setPermissions(newPerms);
            resetAddUser();
        },
        [newPermission, permissions, resetAddUser]
    );

    const onAddUserPermission = useCallback(async () => {
        let userEmail = newUserAliasOrEmail;
        if (!isEmail(newUserAliasOrEmail)) {
            const myEmail = props.auth.getUserInfo()?.email;
            const myEmailDomain = myEmail.split('@')[1];
            userEmail = `${newUserAliasOrEmail}@${myEmailDomain}`;
        }
        const userInput = Object.assign(new ShareAssessmentUserInput(), { userEmail });
        const validationErrors = await validate(userInput);
        setValidationErrors(validationErrors);
        if (!validationErrors.length) {
            const userProfiles = await props.auth.searchUserInfoByEmail(encodeURIComponent(userEmail));
            const supportedUserProfiles = userProfiles.filter((u) => [UserType.AWS, UserType.PARTNER].includes(u.type));
            if (supportedUserProfiles?.length === 0) {
                // unable to find user from cat portal user pool
                const userId = getUserIdFromAmazonUserEmail(userEmail);
                if (userId) {
                    addSelectedUsersAction([
                        {
                            userId,
                            email: userEmail,
                            displayName: userEmail,
                            organizationId: Constants.AWS_ORG_ID,
                        },
                    ]);
                } else {
                    // set invite user email and trigger confirm invitation dialog
                    setInviteUserEmail(userEmail);
                }
            } else if (supportedUserProfiles?.length === 1) {
                // found exactly one user
                addSelectedUsersAction(supportedUserProfiles);
            } else {
                setUsersToSelectFrom(supportedUserProfiles);
            }
        }
    }, [newUserAliasOrEmail, props.auth, addSelectedUsersAction]);

    const addPermissionByAlias = useCallback(async () => {
        const email = `${newUserAlias}@amazon.com`;
        try {
            const userInfos = await props.auth.searchUserInfoByEmail(encodeURIComponent(email));
            if (userInfos?.length === 0) {
                setErrorMessage(appLabels.assessment.share.alias_not_found_message.replace('{0}', newUserAlias));
                setErrorAlertVisible(true);
                return;
            }
            const userProfile = userInfos[0];
            addSelectedUsersAction([
                {
                    userId: userProfile.userId,
                    displayName: userProfile.displayName,
                    email: userProfile.email,
                    organizationId: userProfile.organizationId,
                    organizationName: userProfile.organizationName,
                    name: userProfile.name,
                    type: userProfile.type,
                },
            ]);
        } catch (err: any) {
            rumClient.recordError(err);
            setErrorMessage(appLabels.assessment.share.fail_to_search_user_by_alias);
            setErrorAlertVisible(true);
        }
    }, [newUserAlias, props.auth, addSelectedUsersAction, appLabels]);

    const addPermissionByIamIdentityName = useCallback(async () => {
        const currentUser = props.auth.getUserInfo();
        const userId = constructUserIdFromIamIdentity(currentUser.userId, newUserIamIdentityName);
        try {
            const userInfos = await props.auth.getUserInfoList([userId]);
            if (userInfos?.length === 0) {
                setErrorMessage(appLabels.assessment.share.iam_identity_not_found_message.replace('{0}', newUserIamIdentityName));
                setErrorAlertVisible(true);
                return;
            }
            const userProfile = userInfos[0];
            addSelectedUsersAction([
                {
                    userId: userProfile.userId,
                    displayName: userProfile.displayName,
                    email: userProfile.email,
                    organizationId: userProfile.organizationId,
                    organizationName: userProfile.organizationName,
                    name: userProfile.name,
                    type: userProfile.type,
                },
            ]);
        } catch (err: any) {
            rumClient.recordError(err);
            setErrorMessage(appLabels.assessment.share.fail_to_search_user_by_iam_identity);
            setErrorAlertVisible(true);
        }
    }, [newUserIamIdentityName, props.auth, addSelectedUsersAction, appLabels]);

    const addPermissionByAwsAccountIamIdentity = useCallback(async () => {
        const [accountId, ...rest] = newUserIamIdentityName.split(':');
        const iamIdentity = rest.join(':');
        const userId = `PC30_arn:aws:iam::${accountId}:${iamIdentity}`;
        try {
            const userInfos = await props.auth.getUserInfoList([userId]);
            if (userInfos?.length === 0) {
                setErrorMessage(appLabels.assessment.share.iam_identity_not_found_message.replace('{0}', newUserIamIdentityName));
                setErrorAlertVisible(true);
                return;
            }
            const userProfile = userInfos[0];
            addSelectedUsersAction([
                {
                    userId: userProfile.userId,
                    displayName: userProfile.displayName,
                    email: userProfile.email,
                    organizationId: userProfile.organizationId,
                    organizationName: userProfile.organizationName,
                    name: userProfile.name,
                    type: userProfile.type,
                },
            ]);
        } catch (err: any) {
            rumClient.recordError(err);
            setErrorMessage(appLabels.assessment.share.fail_to_search_user_by_iam_identity);
            setErrorAlertVisible(true);
        }
    }, [newUserIamIdentityName, props.auth, addSelectedUsersAction, appLabels]);

    const onAddUserPermissionClick = useCallback(async () => {
        if (isFeatureEnabled && shareInputType === ShareInputType.IAM_IDENTITY) {
            if (shareUserType === ShareUserType.AWS) {
                await addPermissionByAwsAccountIamIdentity();
            } else {
                await addPermissionByIamIdentityName();
            }
        } else if (isFeatureEnabled && shareUserType === ShareUserType.PC30) {
            await addPermissionByAlias();
        } else {
            await onAddUserPermission();
        }
    }, [
        isFeatureEnabled,
        shareInputType,
        shareUserType,
        addPermissionByAwsAccountIamIdentity,
        addPermissionByIamIdentityName,
        addPermissionByAlias,
        onAddUserPermission,
    ]);

    useEffect(() => {
        // assessment permissions may have changed after assessment is initially loaded
        // reload permissions again
        const loadAssessmentPermissions = async (): Promise<(ResourcePermission | null)[]> => {
            if (a2sClient) {
                const response = await a2sClient.getAssessments(
                    { assessments: [{ id: props.assessment.id }] },
                    {
                        permissions: true,
                    }
                );
                const results = response.data?.getAssessments;
                const assessment: Assessment | null = results?.items ? results?.items[0] : null;
                return assessment?.permissions || [];
            }
            return [];
        };

        const loadUserProfiles = async () => {
            const newPermissions = await loadAssessmentPermissions();
            const userIdsWithPermission: string[] = [];
            newPermissions.forEach((p) => {
                if (p?.userId) {
                    userIdsWithPermission.push(p.userId);
                }
            });
            const userProfiles = await props.auth.getUserInfoList(userIdsWithPermission);
            const shareViewModel: ShareViewModel[] = [];
            newPermissions.forEach((p) => {
                if (p) {
                    const user = userProfiles.find((u) => u.userId === p?.userId);
                    // User invitee email if permission is still pending (e.g. user have not accepted invitation)
                    // Otherwise, extract from profile email or amazon userId
                    const name = user?.name || user?.displayName;
                    const orgName = user?.organizationName || user?.organizationId;
                    const userName = name && orgName ? `${name} (${orgName})` : undefined;
                    const userEmailOrUsername = p.isPending ? p.inviteeEmail : userName;
                    shareViewModel.push({
                        userId: p.userId ?? '',
                        userEmail: userEmailOrUsername,
                        userName: name,
                        userOrgName: orgName,
                        action: p.action,
                        change: p.isPending ? Change.Invited : Change.None,
                        invitationId: p.invitationId,
                    });
                }
            });
            setPermissions(shareViewModel);
        };
        loadUserProfiles();
    }, [props.assessment.id, props.auth]);

    return (
        <Modal
            visible={true}
            header={`${appLabels.assessment.share.share}: '${props.assessment.description}'?`}
            closeAriaLabel={appLabels.user_actions.close_dialog}
            onDismiss={cancelAction}
            footer={
                <Box float='right'>
                    <SpaceBetween direction='horizontal' size='xs'>
                        <Button variant='link' onClick={cancelAction}>
                            {appLabels.user_actions.cancel}
                        </Button>
                        <Button
                            variant='primary'
                            loading={updateInProgress}
                            onClick={() =>
                                onConfirmShare({
                                    newPermissions: permissions.filter((p) => p.change === Change.Add || p.change === Change.Invite),
                                    changedPermissions: permissions.filter((p) => p.change === Change.Update),
                                    deletedPermissions: permissions.filter((p) => p.change === Change.Delete),
                                })
                            }
                            disabled={permissions.filter((p) => p.change !== Change.None).length === 0}
                        >
                            {appLabels.user_actions.apply}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <ColumnLayout borders='horizontal'>
                <div>
                    <TextContent>
                        <h4>{appLabels.assessment.share.current_permissions}</h4>
                    </TextContent>
                    <ShareTable setPermissions={setPermissions} permissions={permissions} />
                </div>
                <Container
                    header={
                        <Header
                            variant='h2'
                            actions={
                                <SpaceBetween direction='horizontal' size='xs'>
                                    <Button
                                        disabled={
                                            isFeatureEnabled && shareInputType === ShareInputType.IAM_IDENTITY
                                                ? !newUserIamIdentityName || !!iamIdentityNameErrorMessage
                                                : isFeatureEnabled && shareUserType === ShareUserType.PC30
                                                ? !newUserAlias || !!aliasErrorMessage
                                                : !validUserName
                                        }
                                        onClick={onAddUserPermissionClick}
                                        data-testid='add-user-button'
                                    >
                                        {appLabels.assessment.share.action_add}
                                    </Button>
                                </SpaceBetween>
                            }
                        >
                            {appLabels.assessment.share.share_this_assessment}
                        </Header>
                    }
                >
                    {isFeatureEnabled && (shareUserType === ShareUserType.PC30 || shareUserType === ShareUserType.AWS) && (
                        <Tiles
                            data-testid='share-input-type-tiles'
                            value={shareInputType}
                            onChange={(e) => setShareInputType(e.detail.value as ShareInputType)}
                            items={
                                shareUserType === ShareUserType.PC30
                                    ? [
                                          { label: appLabels.assessment.share.share_with_iam_identity, value: ShareInputType.IAM_IDENTITY },
                                          { label: appLabels.assessment.share.share_with_amazon_employee, value: ShareInputType.EMAIL },
                                      ]
                                    : [
                                          { label: appLabels.assessment.share.share_with_pc_iam_identity, value: ShareInputType.IAM_IDENTITY },
                                          { label: appLabels.assessment.share.share_with_aws_or_apn_by_email, value: ShareInputType.EMAIL },
                                      ]
                            }
                        />
                    )}
                    {isFeatureEnabled && shareInputType === ShareInputType.IAM_IDENTITY ? (
                        <FormField errorText={iamIdentityNameErrorMessage}>
                            <Input
                                data-testid='iam-identity-input'
                                invalid={!!iamIdentityNameErrorMessage}
                                placeholder={
                                    shareUserType === ShareUserType.AWS
                                        ? appLabels.assessment.share.aws_iam_identity_placeholder
                                        : appLabels.assessment.share.iam_identity_placeholder
                                }
                                onChange={(event) => {
                                    const value = event.detail.value;
                                    if (shareUserType === ShareUserType.AWS) {
                                        const isValid = /^\d{12}:(role|user)\/[\w+=,.@-]+$/.test(value);
                                        setIamIdentityNameErrorMessage(
                                            value && !isValid ? appLabels.assessment.share.aws_iam_identity_format_invalid : ''
                                        );
                                    } else {
                                        setIamIdentityNameErrorMessage(validateIamIdentityName(value));
                                    }
                                    setNewUserIamIdentityName(value);
                                }}
                                value={newUserIamIdentityName}
                            />
                        </FormField>
                    ) : isFeatureEnabled && shareUserType === ShareUserType.PC30 ? (
                        <FormField errorText={aliasErrorMessage}>
                            <Input
                                data-testid='alias-input'
                                invalid={!!aliasErrorMessage}
                                placeholder={appLabels.assessment.share.alias_placeholder}
                                onChange={(event) => {
                                    const value = event.detail.value;
                                    setAliasErrorMessage(value ? '' : appLabels.assessment.share.alias_required);
                                    setNewUserAlias(value);
                                }}
                                value={newUserAlias}
                            />
                        </FormField>
                    ) : (
                        <FormField label={appLabels.assessment.share.username} errorText={getErrorText('userEmail')}>
                            <Input
                                placeholder={appLabels.assessment.share.username_placeholder}
                                value={newUserAliasOrEmail}
                                onChange={(event) => {
                                    setUserAliasOrEmail(event.detail.value);
                                }}
                            />
                        </FormField>
                    )}
                    <FormField label={appLabels.assessment.share.permission}>
                        <Select
                            onChange={(event) => setNewPermission(event.detail.selectedOption.label as AssessmentResourceAction)}
                            selectedOption={{ label: newPermission }}
                            options={[
                                { label: AssessmentResourceAction.FULL },
                                { label: AssessmentResourceAction.UPDATE },
                                { label: AssessmentResourceAction.VIEW },
                            ]}
                        />
                    </FormField>
                    {isFeatureEnabled && shareUserType === ShareUserType.PC30 ? (
                        <Alert type='info' dismissible={false} header={appLabels.assessment.share.share_info_header}>
                            {appLabels.assessment.share.share_info_message}
                        </Alert>
                    ) : isFeatureEnabled && shareUserType === ShareUserType.AWS && shareInputType === ShareInputType.IAM_IDENTITY ? (
                        <Alert type='info' dismissible={false} header={appLabels.assessment.share.aws_iam_identity_info_header}>
                            {appLabels.assessment.share.aws_iam_identity_info_message}
                        </Alert>
                    ) : null}
                </Container>

                <Alert
                    onDismiss={() => setErrorAlertVisible(false)}
                    visible={errorAlertVisible}
                    dismissAriaLabel={appLabels.user_actions.close_alert}
                    dismissible
                    type='error'
                    header={appLabels.assessment.share.error_update_permissions}
                >
                    {errorMessage}
                </Alert>
            </ColumnLayout>
            <ShareInvitationConfirmationDialog
                inviteeEmail={inviteUserEmail}
                assessmentDescription={props.assessment.description}
                confirmAction={inviteUserAction}
                cancelAction={() => setInviteUserEmail('')}
            />
            <ShareMultiUserSelectDialog
                usersToSelectFrom={usersToSelectFrom}
                assessmentDescription={props.assessment.description}
                addSelectedUsersAction={addSelectedUsersAction}
                cancelAction={() => setUsersToSelectFrom([])}
            />
        </Modal>
    );
};

export default withAppLabelsContext(withAuthContext(ShareAssessmentDialog));
